<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { GetDetailProductResponse } from '@/services/pcsServiceTypes'
import ProductPhotoGallery from './_ProductPhotoGallery.vue'

const { t } = useI18n()

const props = defineProps<{
	product: GetDetailProductResponse | null
}>()

const isProduction = import.meta.env.PROD
const isProductionURL = isProduction ? '/shop/search' : '/search'

const productImages = computed(() => {
	// Data might contain empty strings, so we need to filter them out first
	const filteredImages =
		props.product?.brief.images?.filter(image => image.trim() !== '') || []

	return filteredImages.length > 0
		? filteredImages
		: [
				'https://test-hk.buynship.com/contents/uploads/2024/02/pcs-default-image.png',
		  ] // Default image
})

const categoryParam = ref<string | null>(null)

const setCategoryParam = () => {
	const category = new URLSearchParams(window.location.search).get('categories')
	if (!category) return

	categoryParam.value = category
}

const breadcrumbItems = computed(() => {
	return [
		{ text: t('pcs_breadcrumb_home'), link: '/shop' },
		{ text: 'Mobile', link: `${isProductionURL}?categories=mobile` },
	]
})

const displayedSourcesCount = computed(() => {
	const count = props.product?.brief.count
	if (!count) return t('pcs_no_source_found')

	const sourceCount = count > 99 ? '99+' : count
	if (count === 1) return t('pcs_one_source_found')
	return t('pcs_multiple_sources_found', { count: sourceCount })
})

const displayedCurrency = ref('HKD$')
const displayedPrice = computed(() => {
	const price = props.product?.brief.lowest_price
	if (!price) return ''
	return Number(price).toLocaleString(undefined, {
		minimumFractionDigits: 2,
	})
})

onMounted(async () => {
	setCategoryParam()
})
</script>

<template>
	<div class="bs-container">
		<div class="col-span-12 pb-casual-friends pt-acquaintances text-n-950">
			<nav aria-label="Breadcrumb">
				<ol class="flex pl-0">
					<li
						v-for="(item, index) in breadcrumbItems"
						:key="index"
						class="typography-s1-reg flex items-center gap-4px"
					>
						<a v-if="item.link" :href="item.link" class="capitalize text-n-950">
							{{ item.text }}
						</a>
						<span v-else class="capitalize">{{ item.text }}</span>

						<span
							v-if="index < breadcrumbItems.length - 1"
							class="px-8px text-n-300"
						>
							/
						</span>
					</li>
				</ol>
			</nav>

			<div
				class="my-casual-friends flex flex-col gap-20px mdp:flex-row mdp:gap-16px"
			>
				<ProductPhotoGallery v-if="productImages" :images="productImages" />

				<div class="w-full">
					<h1 class="typography-h3-str mb-good-friends">
						{{ product?.brief.title }}
					</h1>
					<div class="typography-s1-reg rounded-lg bg-wg-50 p-20px">
						<div
							class="mb-12px w-max rounded-lg border border-n-100 px-8px py-4px text-n-500"
						>
							{{ displayedSourcesCount }}
						</div>
						<div class="flex items-center gap-12px">
							<div class="typography-s1-emp">
								{{ t('pcs_price_starts_from') }}
							</div>
							<div class="typography-h4-str text-t-600">
								{{ displayedCurrency }} {{ displayedPrice }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
